import { create } from 'zustand';
import { registerUserScopedReset } from './session-read';
import type {
  SentenceAnalysis,
  TransformationModal,
  RefactoredResult,
  ChatMessage,
  TransformRequest,
} from '../types';

// ============================================
// The transform conversation
// ============================================

/**
 * The most turns one transform request carries. The dialog keeps and shows
 * every turn; past this the request keeps the first round (the pitfall's own
 * question and the first answer, which frame the rest) and the latest rounds.
 */
export const MAX_REQUEST_HISTORY = 20;

/**
 * The request's share of the transcript. The transcript alternates
 * assistant/user from the coach in whole rounds, so dropping whole rounds from
 * the middle keeps that alternation (the server appends the question being
 * asked as the next assistant turn).
 */
export function capRequestHistory(history: ChatMessage[]): ChatMessage[] {
  if (history.length <= MAX_REQUEST_HISTORY) return history;
  return [...history.slice(0, 2), ...history.slice(-(MAX_REQUEST_HISTORY - 2))];
}

/**
 * Asked when the model says "not enough yet" without saying what it needs.
 * A question, because the dialog shows it as the next question.
 */
export const FALLBACK_FOLLOW_UP =
  'Could you tell me a little more about how you felt in that moment, and what mattered to you?';

/** The question on screen now: the coach's follow-up, else the pitfall's own. */
function questionBeingAsked(modal: TransformationModal): string | null {
  return modal.followUpQuestion ?? modal.currentPitfall?.question ?? null;
}

// ============================================
// Type Definitions
// ============================================

interface GiraffeTranslatorState {
  inputText: string;
  analysisResults: SentenceAnalysis[] | null;
  originalAnalysisResults: SentenceAnalysis[] | null;
  isAnalyzing: boolean;
  isTransforming: boolean;
  error: string | null;
  modal: TransformationModal;
  showSuccessAnimation: boolean;
  transformedSentences: Record<number, string>;
  removedSentences: Set<number>;
  isRefactoring: boolean;
  refactoredResult: RefactoredResult | null;
  showTransformationCard: boolean;
}

interface GiraffeTranslatorActions {
  setInputText: (text: string) => void;
  reset: () => void;
  clearAnalysis: () => void;
  setAnalysisResults: (results: SentenceAnalysis[]) => void;
  setIsAnalyzing: (isAnalyzing: boolean) => void;
  setError: (error: string | null) => void;
  openModal: (sentenceIndex: number) => void;
  closeModal: () => void;
  beginTransformation: () => void;
  setUserAnswer: (answer: string) => void;
  /** What the next transform request sends; null when nothing can be asked. */
  getTransformRequest: () => TransformRequest | null;
  /** The answer to `sent` was enough: the round joins the transcript. */
  handleTransformationSuccess: (giraffeSentence: string, sent?: TransformRequest) => void;
  /**
   * The answer to `sent` was not enough yet: the round joins the transcript
   * and `followUp` (the coach's reason, ending in a question) is asked next.
   */
  handleTransformationInsufficient: (followUp: string, sent?: TransformRequest) => void;
  setIsTransforming: (isTransforming: boolean) => void;
  updateSentence: () => void;
  triggerSuccessAnimation: () => void;
  hideSuccessAnimation: () => void;
  getSentencesWithPitfalls: () => SentenceAnalysis[];
  getTotalPitfallCount: () => number;
  areAllSentencesClean: () => boolean;
  saveTransformedSentence: (sentenceIndex: number, transformedText: string) => void;
  removeSentence: (sentenceIndex: number) => void;
  areAllSentencesTransformed: () => boolean;
  getTransformedSentenceCount: () => number;
  setIsRefactoring: (isRefactoring: boolean) => void;
  startRefactoring: () => void;
  completeRefactoring: (result: RefactoredResult) => void;
  resetToNew: () => void;
}

type GiraffeTranslatorStore = GiraffeTranslatorState & GiraffeTranslatorActions;

// ============================================
// Initial State
// ============================================

const initialModalState: TransformationModal = {
  isOpen: false,
  currentState: null,
  sentenceIndex: null,
  currentPitfall: null,
  userAnswer: '',
  transformedSentence: null,
  followUpQuestion: null,
  conversationHistory: [],
};

/**
 * The transcript with one more round: the question that was asked and the
 * answer that was sent, exactly as the request carried them.
 */
function withRound(modal: TransformationModal, sent: TransformRequest): ChatMessage[] {
  return [
    ...modal.conversationHistory,
    { role: 'assistant', content: sent.questionAsked },
    { role: 'user', content: sent.userAnswer },
  ];
}

// ============================================
// Store Implementation
// ============================================

export const useGiraffeTranslatorStore = create<GiraffeTranslatorStore>((set, get) => ({
  // State
  inputText: '',
  analysisResults: null,
  originalAnalysisResults: null,
  isAnalyzing: false,
  isTransforming: false,
  error: null,
  modal: initialModalState,
  showSuccessAnimation: false,
  transformedSentences: {},
  removedSentences: new Set(),
  isRefactoring: false,
  refactoredResult: null,
  showTransformationCard: false,

  // Basic Actions
  setInputText: (text: string) => set({ inputText: text }),

  reset: () =>
    set({
      inputText: '',
      analysisResults: null,
      originalAnalysisResults: null,
      isAnalyzing: false,
      isTransforming: false,
      error: null,
      modal: initialModalState,
      showSuccessAnimation: false,
      transformedSentences: {},
      removedSentences: new Set(),
      isRefactoring: false,
      refactoredResult: null,
      showTransformationCard: false,
    }),

  clearAnalysis: () =>
    set({
      analysisResults: null,
      originalAnalysisResults: null,
      error: null,
      modal: initialModalState,
      transformedSentences: {},
      removedSentences: new Set(),
      isRefactoring: false,
      refactoredResult: null,
      showTransformationCard: false,
    }),

  // Analysis Actions
  setAnalysisResults: (results: SentenceAnalysis[]) => {
    const currentOriginal = get().originalAnalysisResults;

    set({
      analysisResults: results,
      originalAnalysisResults: currentOriginal || JSON.parse(JSON.stringify(results)),
      error: null,
    });

    // Check if there are no pitfalls at all
    const hasPitfalls = results.some((item) => item.pitfalls && item.pitfalls.length > 0);
    if (!hasPitfalls) {
      get().triggerSuccessAnimation();
    }
  },

  setIsAnalyzing: (isAnalyzing: boolean) => set({ isAnalyzing }),

  setError: (error: string | null) => {
    if (error) {
      set({
        error,
        isAnalyzing: false,
        isTransforming: false,
      });
    } else {
      set({ error: null });
    }
  },

  // Modal Actions
  openModal: (sentenceIndex: number) => {
    const results = get().analysisResults;
    if (!results || !results[sentenceIndex]) return;

    const sentence = results[sentenceIndex];
    const firstPitfall = sentence.pitfalls[0];

    set({
      modal: {
        isOpen: true,
        currentState: 'pitfall-details',
        sentenceIndex,
        currentPitfall: firstPitfall,
        userAnswer: '',
        transformedSentence: null,
        followUpQuestion: null,
        conversationHistory: [],
      },
    });
  },

  closeModal: () => set({ modal: initialModalState }),

  beginTransformation: () => {
    const modal = get().modal;
    if (!modal.currentPitfall) return;

    set({
      modal: {
        ...modal,
        currentState: 'question',
        userAnswer: '',
        followUpQuestion: null,
        conversationHistory: [],
      },
    });
  },

  setUserAnswer: (answer: string) => {
    const modal = get().modal;
    set({
      modal: {
        ...modal,
        userAnswer: answer,
      },
    });
  },

  getTransformRequest: (): TransformRequest | null => {
    const { modal } = get();
    const questionAsked = questionBeingAsked(modal);
    if (!questionAsked) return null;
    return {
      questionAsked,
      userAnswer: modal.userAnswer,
      conversationHistory: capRequestHistory(modal.conversationHistory),
    };
  },

  handleTransformationSuccess: (giraffeSentence: string, sent?: TransformRequest) => {
    const modal = get().modal;
    const round = sent ?? get().getTransformRequest();
    set({
      modal: {
        ...modal,
        currentState: 'success',
        transformedSentence: giraffeSentence,
        // The last answer stays in the transcript above the result; the
        // reply box keeps it too (the dialogue training row's final answer).
        userAnswer: round ? round.userAnswer : modal.userAnswer,
        conversationHistory: round ? withRound(modal, round) : modal.conversationHistory,
      },
      isTransforming: false,
    });
  },

  handleTransformationInsufficient: (followUp: string, sent?: TransformRequest) => {
    const modal = get().modal;
    const round = sent ?? get().getTransformRequest();
    set({
      modal: {
        ...modal,
        // Same step: the follow-up is simply the next question.
        currentState: 'question',
        followUpQuestion: followUp.trim() ? followUp : FALLBACK_FOLLOW_UP,
        userAnswer: '',
        conversationHistory: round ? withRound(modal, round) : modal.conversationHistory,
      },
      isTransforming: false,
    });
  },

  setIsTransforming: (isTransforming: boolean) => set({ isTransforming }),

  updateSentence: () => {
    const { modal, analysisResults } = get();

    if (!modal.transformedSentence || modal.sentenceIndex === null || !analysisResults) return;

    // Save the transformed sentence
    get().saveTransformedSentence(modal.sentenceIndex, modal.transformedSentence);

    // Create a copy of the analysis results
    const updatedResults = [...analysisResults];

    // Update the specific sentence
    updatedResults[modal.sentenceIndex] = {
      sentence: modal.transformedSentence,
      pitfalls: [],
    };

    // Update the inputText to reflect the change
    const updatedText = updatedResults.map((item) => item.sentence).join(' ');

    set({
      analysisResults: updatedResults,
      inputText: updatedText,
    });

    // Close the modal
    get().closeModal();

    // Check if all sentences are now pitfall-free
    const remainingPitfalls = updatedResults.some(
      (item) => item.pitfalls && item.pitfalls.length > 0,
    );
    if (!remainingPitfalls) {
      get().triggerSuccessAnimation();
    }
  },

  // Success Animation Actions
  triggerSuccessAnimation: () => {
    set({ showSuccessAnimation: true });

    setTimeout(() => {
      set({ showSuccessAnimation: false });
    }, 3000);
  },

  hideSuccessAnimation: () => set({ showSuccessAnimation: false }),

  // Utility Functions
  getSentencesWithPitfalls: (): SentenceAnalysis[] => {
    const results = get().analysisResults;
    if (!results) return [];

    return results.filter((item) => item.pitfalls && item.pitfalls.length > 0);
  },

  getTotalPitfallCount: (): number => {
    const results = get().analysisResults;
    if (!results) return 0;

    return results.reduce((count, item) => {
      return count + (item.pitfalls ? item.pitfalls.length : 0);
    }, 0);
  },

  areAllSentencesClean: (): boolean => {
    const results = get().analysisResults;
    if (!results) return false;

    return !results.some((item) => item.pitfalls && item.pitfalls.length > 0);
  },

  // Refactoring Actions
  saveTransformedSentence: (sentenceIndex: number, transformedText: string) => {
    const transformedSentences = get().transformedSentences;
    set({
      transformedSentences: {
        ...transformedSentences,
        [sentenceIndex]: transformedText,
      },
    });
  },

  removeSentence: (sentenceIndex: number) => {
    const removedSentences = new Set(get().removedSentences);
    removedSentences.add(sentenceIndex);

    set({ removedSentences });

    // Close the modal
    get().closeModal();
  },

  areAllSentencesTransformed: (): boolean => {
    const { analysisResults, transformedSentences, removedSentences } = get();
    if (!analysisResults) return false;

    const sentencesWithPitfalls = analysisResults
      .map((item, index) => ({ index, hasPitfalls: item.pitfalls && item.pitfalls.length > 0 }))
      .filter((item) => item.hasPitfalls)
      .map((item) => item.index);

    return sentencesWithPitfalls.every(
      (index) => transformedSentences[index] !== undefined || removedSentences.has(index),
    );
  },

  getTransformedSentenceCount: (): number => {
    return Object.keys(get().transformedSentences).length;
  },

  setIsRefactoring: (isRefactoring: boolean) => set({ isRefactoring }),

  startRefactoring: () => {
    set({ isRefactoring: true, error: null });
  },

  completeRefactoring: (result: RefactoredResult) => {
    set({
      refactoredResult: result,
      isRefactoring: false,
      showTransformationCard: true,
    });
  },

  resetToNew: () => {
    set({
      inputText: '',
      analysisResults: null,
      originalAnalysisResults: null,
      transformedSentences: {},
      refactoredResult: null,
      showTransformationCard: false,
      error: null,
      modal: initialModalState,
    });
  },
}));

// The text, its analysis and the dialogue are the signed-in account's own words
// (David, 2026-10-04): reset synchronously on every session change, with every
// per-user store (session-read.ts). The page's requests are session-bound too
// (giraffe-translator.tsx), so a late answer for A never refills it for B.
registerUserScopedReset(() => useGiraffeTranslatorStore.getState().reset());
